import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import SystemDataService from '../../Services/SystemDataService';
import Image from '../Image/image-component';

/**
 * LoadingAnimation - YJP Palm Beach Industrial Sync Loader
 * Flat Industrial Design (No Shadows / Redux Message Sync)
 */
export function LoadingAnimation({ fullscreen = false, loadingMessage: parentMessage }) {
  const [progress, setProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [shouldRender, setShouldRender] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);

  // --- Redux Hydration ---
  const systemData = useSelector((s) => s.systemData || {});
  const { dir = 'ltr', links = [], loadingMessage: reduxMessage } = systemData;

  const logo = SystemDataService.byLabel(links, 'Logo');
  const loadingAnimImg = SystemDataService.byLabel(links, 'Loading Animation');
  const isRtl = dir === 'rtl';

  // Resolve Message: Parent Prop > Redux > Fallback
  const finalMessage = parentMessage || reduxMessage;

  useEffect(() => {
    // 1. Snappy Progress Simulation
    const timer = setInterval(() => {
      setProgress((prev) => {
        const increment = prev < 30 ? 15 : prev < 70 ? 5 : prev < 90 ? 1 : 0.2;
        return prev + Math.random() * increment;
      });
    }, 180);

    // 2. Smooth Exit Sequence
    const completeTimeout = setTimeout(() => {
      setProgress(100);
      setTimeout(() => {
        setIsVisible(false); // Start Fade
        setTimeout(() => setShouldRender(false), 600); // Unmount
      }, 300);
    }, 1500);

    return () => {
      clearInterval(timer);
      clearTimeout(completeTimeout);
    };
  }, []);

  if (!shouldRender) return null;

  // --- RENDER 1: FULL-SCREEN PORTAL ---
  if (fullscreen) {
    return (
      <div
        dir={dir}
        className={`
                    fixed inset-0 z-[9999] flex flex-col items-center justify-center 
                    bg-white dark:bg-black transition-all duration-700 ease-in-out
                    ${isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none scale-105'}
                `}
      >
        {/* Engineering Grid (The Blueprint vibe) */}
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none bg-[radial-gradient(theme(colors.primary.900)_1px,transparent_1px)] [background-size:40px_40px]"></div>

        <div className="relative flex flex-col items-center z-10 w-full max-w-xs">

          {/* Branded Logo Entrance */}
          <div className="mb-8 w-28 lg:w-28 h-auto overflow-hidden">
            <Image
              src={loadingAnimImg?.image || logo?.image}
              alt="Syncing"
              useSekeleton={false}
              onLoad={() => setImageLoaded(true)}
              className={`h-full object-contain transition-all duration-1000 ease-[cubic-bezier(0.23,1,0.32,1)] transform 
                                ${imageLoaded ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-90 translate-y-8'}`}
            />
          </div>

          {/* Registry Status Section */}
          <div className={`flex flex-col items-center w-full transition-opacity duration-700 delay-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}>
            {finalMessage && (
              <h3
                className="text-[10px] font-bold text-primary-950/40 dark:text-white/40 tracking-[0.5em] uppercase italic mb-6"
                
              >
                {finalMessage}
              </h3>
            )}


            {/* Centered Technical Tracker (The "Google" Flat Line) */}
            <div className="w-32 h-[2px] bg-gray-100 dark:bg-neutral-800 rounded-full overflow-hidden relative">
              <div
                className="h-full bg-secondary-500 transition-all duration-300 ease-out"
                style={{
                  width: `${progress}%`,
                  [isRtl ? 'right' : 'left']: 0
                }}
              />
            </div>

          </div>
        </div>
      </div>
    );
  }

  // --- RENDER 2: GOOGLE-THEMED TOP BAR (Non-Fullscreen) ---
  return (
    <div
      className={`
                fixed top-0 z-[9999] h-[2px] bg-secondary-500 
                transition-all duration-500 ease-out
                ${isVisible ? 'opacity-100' : 'opacity-0'}
            `}
      style={{
        width: `${progress}%`,
        left: isRtl ? 'auto' : 0,
        right: isRtl ? 0 : 'auto'
      }}
    />
  );
}